import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface TenantSummary {
  id: string;
  slug: string;
  name: string;
  plan: string;
  status: string;
  created_at: string;
  is_demo: boolean;
  total_users: number;
  mau: number;
  wau: number;
  pilot_mode: boolean;
}

export interface ActivityEntry {
  activity_id: string;
  action_type: string;
  page_path: string;
  created_at: string;
  user_email: string;
  tenant_slug: string;
  tenant_name: string;
}

export interface SuperadminUser {
  user_id: string;
  email: string;
  created_at: string;
  tenant_id: string | null;
  tenant_slug: string;
  tenant_name: string;
  role: string;
  last_active: string | null;
}

export interface PlanDistribution {
  plan: string;
  count: number;
}

export interface TenantGrowth {
  month: string;
  new_tenants: number;
}

export interface AuditLogEntry {
  id: string;
  action: string;
  created_at: string;
  actor_email: string | null;
  target_tenant_name: string | null;
  target_tenant_slug: string | null;
  target_user_email: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}

export function useSuperadminCheck() {
  const { user, loading } = useAuth();
  const [isSuperadmin, setIsSuperadmin] = useState<boolean | null>(null);

  useEffect(() => {
    if (loading) return;                         // wait for auth to settle
    if (!user) { setIsSuperadmin(false); return; }
    supabase.rpc('check_superadmin').then(({ data }) => {
      setIsSuperadmin(data ?? false);
    });
  }, [user, loading]);

  return isSuperadmin;
}

export function useSuperadminDashboard() {
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [planDist, setPlanDist] = useState<PlanDistribution[]>([]);
  const [growth, setGrowth] = useState<TenantGrowth[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [t, a, p, g] = await Promise.all([
      supabase.rpc('get_sa_tenant_summary'),
      supabase.rpc('get_sa_activity_feed', { p_limit: 20 }),
      supabase.rpc('get_sa_plan_distribution'),
      supabase.rpc('get_sa_tenant_growth'),
    ]);
    setTenants((t.data as TenantSummary[]) ?? []);
    setActivity((a.data as ActivityEntry[]) ?? []);
    setPlanDist((p.data as PlanDistribution[]) ?? []);
    setGrowth((g.data as TenantGrowth[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const totalUsers = tenants.reduce((s, t) => s + Number(t.total_users), 0);
  const totalMAU   = tenants.reduce((s, t) => s + Number(t.mau), 0);

  return { tenants, activity, planDist, growth, loading, totalUsers, totalMAU, refetch: load };
}

export function useSuperadminUsers() {
  const [users, setUsers] = useState<SuperadminUser[]>([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    supabase.rpc('get_sa_all_users').then(({ data }) => {
      setUsers((data as SuperadminUser[]) ?? []);
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, []);

  return { users, loading, refetch: load };
}

export async function setTenantStatus(tenantId: string, status: 'active' | 'suspended') {
  const { error } = await supabase.rpc('sa_set_tenant_status', { p_tenant_id: tenantId, p_status: status });
  if (error) throw error;
}

export async function setTenantPlan(tenantId: string, plan: string) {
  const { error } = await supabase.rpc('sa_set_tenant_plan', { p_tenant_id: tenantId, p_plan: plan });
  if (error) throw error;
}

export async function setTenantPilotMode(tenantId: string, enabled: boolean) {
  const { error } = await supabase.rpc('sa_set_tenant_pilot_mode', { p_tenant_id: tenantId, p_enabled: enabled });
  if (error) throw error;
}

export async function setTenantDemoMode(tenantId: string, isDemo: boolean) {
  const { error } = await supabase.rpc('sa_set_tenant_demo_mode', { p_tenant_id: tenantId, p_is_demo: isDemo });
  if (error) throw error;
}

export interface DemoDataCounts {
  projects_sync: number;
  tasks_sync: number;
  kpis_sync: number;
  meetings_sync: number;
  emails_sync: number;
  risk_signals: number;
  documents: number;
  integration_configs: number;
  ai_outputs: number;
  decisions: number;
  decision_audit_logs: number;
  user_activities: number;
}

export interface DemoDataResult {
  counts: DemoDataCounts;
  success?: boolean;
  errors?: Array<{ table: string; message: string }>;
}

export type DemoLocale = "en" | "ar-SA";

async function invokeDemoData(action: "status" | "populate" | "flush", tenantId: string, locale?: DemoLocale): Promise<DemoDataResult> {
  const { data, error } = await supabase.functions.invoke("superadmin-demo-data", {
    body: { action, tenantId, locale },
  });
  if (error) throw new Error(error.message);
  return data as DemoDataResult;
}

export function getDemoDataStatus(tenantId: string) {
  return invokeDemoData("status", tenantId);
}

export function populateDemoData(tenantId: string, locale: DemoLocale = "en") {
  return invokeDemoData("populate", tenantId, locale);
}

export function flushDemoData(tenantId: string) {
  return invokeDemoData("flush", tenantId);
}

export async function setUserRole(userId: string, tenantId: string, role: string) {
  const { error } = await supabase.rpc('sa_set_user_role', { p_user_id: userId, p_tenant_id: tenantId, p_role: role });
  if (error) throw error;
}

export async function removeUserFromTenant(userId: string, tenantId: string) {
  const { error } = await supabase.rpc('sa_remove_user_from_tenant', { p_user_id: userId, p_tenant_id: tenantId });
  if (error) throw error;
}

export function useSuperadminAuditLog() {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.rpc('get_sa_audit_log', { p_limit: 20 }).then(({ data }) => {
      setEntries((data as AuditLogEntry[]) ?? []);
      setLoading(false);
    });
  }, []);

  return { entries, loading };
}

export interface PlatformAnalytics {
  days: number;
  generated_at: string;
  totals: { tenants: number; active_tenants: number; active_users: number; actions: number; dau: number; wau: number; mau: number };
  daily: Array<{ date: string; actions: number; users: number; tenants: number }>;
  top_views: Array<{ name: string; count: number }>;
  action_types: Array<{ name: string; count: number }>;
  top_tenants: Array<{ tenant_id: string; name: string; slug: string; plan: string; actions: number; users: number; last_active: string }>;
}

export interface DailyPoint { date: string; actions: number; users: number }
export interface NameCount { name: string; count: number }

export interface OrgAnalytics {
  days: number;
  org: { id: string; name: string; slug: string; plan: string; status: string; created_at: string; members: number };
  totals: { actions: number; active_users: number; dau: number; wau: number; mau: number };
  daily: DailyPoint[];
  top_views: NameCount[];
  action_types: NameCount[];
  users: Array<{ user_id: string; email: string | null; actions: number; last_active: string; top_view: string | null }>;
}

export interface UserAnalytics {
  days: number;
  org: { id: string; name: string; slug: string };
  user: { id: string; email: string; role: string | null };
  totals: { actions: number; active_days: number; last_active: string | null };
  daily: DailyPoint[];
  top_views: NameCount[];
  action_types: NameCount[];
  recent: Array<{ at: string; action_type: string; page_path: string | null; view: string | null }>;
}

export interface ViewBreakdown {
  view: string;
  days: number;
  totals: { uses: number; users: number; tenants: number };
  daily: DailyPoint[];
  tenants: Array<{ tenant_id: string; name: string; slug: string; plan: string; uses: number; users: number; last_used: string }>;
}

type SaFn = 'sa_platform_analytics' | 'sa_org_analytics' | 'sa_user_analytics' | 'sa_view_breakdown';

/** Loads one superadmin-gated analytics RPC; re-runs when the arguments change. */
function useSaRpc<T>(fn: SaFn, args: Record<string, unknown>) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const key = JSON.stringify(args);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    // The generated RPC types are per-function; the wrapper types the result for each caller.
    (supabase.rpc as unknown as (f: string, a: Record<string, unknown>) => PromiseLike<{ data: unknown; error: { message: string } | null }>)(fn, args)
      .then(({ data: d, error: e }) => {
        if (cancelled) return;
        if (e) setError(e.message); else setData(d as T);
        setLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fn, key]);

  return { data, loading, error };
}

/** Cross-tenant usage aggregates (superadmin-only RPC; counts only, no user-level rows). */
export const useSuperadminAnalytics = (days: number) => useSaRpc<PlatformAnalytics>('sa_platform_analytics', { p_days: days });
/** One organisation's usage, including its users (audited on the server). */
export const useOrgAnalytics = (tenantId: string, days: number) => useSaRpc<OrgAnalytics>('sa_org_analytics', { p_tenant: tenantId, p_days: days });
/** One user's usage inside an organisation (audited on the server). */
export const useUserAnalytics = (tenantId: string, userId: string, days: number) => useSaRpc<UserAnalytics>('sa_user_analytics', { p_tenant: tenantId, p_user: userId, p_days: days });
/** Which organisations use a given screen. */
export const useViewBreakdown = (view: string, days: number) => useSaRpc<ViewBreakdown>('sa_view_breakdown', { p_view: view, p_days: days });

export interface UserEvent { at: string; action_type: string; page_path: string | null; view: string | null }
interface UserEventsPage { total: number; limit: number; offset: number; events: UserEvent[] }

const EVENTS_PAGE = 50;

/** Filtered, paged event timeline for one user (server-side filters; first page is audited). */
export function useUserEvents(tenantId: string, userId: string, days: number, day: string | null, actionType: string | null) {
  const [events, setEvents] = useState<UserEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const key = JSON.stringify([tenantId, userId, days, day, actionType]);

  const fetchPage = async (offset: number): Promise<UserEventsPage> => {
    const { data, error: e } = await (supabase.rpc as unknown as (f: string, a: Record<string, unknown>) => PromiseLike<{ data: unknown; error: { message: string } | null }>)('sa_user_events', {
      p_tenant: tenantId, p_user: userId, p_days: days, p_day: day, p_action_type: actionType, p_limit: EVENTS_PAGE, p_offset: offset,
    });
    if (e) throw new Error(e.message);
    return data as UserEventsPage;
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPage(0)
      .then((page) => { if (!cancelled) { setEvents(page.events); setTotal(page.total); } })
      .catch((e: Error) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const page = await fetchPage(events.length);
      setEvents((prev) => [...prev, ...page.events]);
      setTotal(page.total);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoadingMore(false);
    }
  };

  return { events, total, loading, loadingMore, error, loadMore, hasMore: events.length < total };
}
